import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Trophy, TrendingUp, Calendar, Award, Users, Target, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import Leaderboard from '../components/Leaderboard';
import { getLeaderboard, getRaces } from '../services/api';
import { getCircuitLayoutUrl, FILTER_GOLD, FILTER_DARK_BLUE, LOCAL_PNG_GOLD_FILTER, LOCAL_PNG_DARK_BLUE_FILTER } from '../utils/circuitLayouts';
import { getRaceFlagUrl } from '../utils/countryFlags';
import { useTheme } from '../contexts/ThemeContext';
import { getNextSession, isDeadlinePassed, formatLocalTime, utcToDate } from '../utils/timeUtils';

const Home = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [nextRace, setNextRace] = useState(null);
  const [allRaces, setAllRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPosition, setUserPosition] = useState(null);
  const [userPoints, setUserPoints] = useState(null);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [nextSessionInfo, setNextSessionInfo] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const { theme } = useTheme();

  useEffect(() => {
    const fetchRaces = async () => {
      try {
        const races = await getRaces(2026);
        setAllRaces(races || []);
        const now = new Date();
        const upcoming = (races || [])
          .filter(r => r.status === 'upcoming' && (utcToDate(r.date, r.time || '23:59') || now) >= now)
          .sort((a, b) => utcToDate(a.date, a.time || '12:00') - utcToDate(b.date, b.time || '12:00'));
        if (upcoming.length > 0) {
          setNextRace(upcoming[0]);
        }
        // Find next session across all upcoming races
        const session = getNextSession(races || []);
        setNextSessionInfo(session);
      } catch (error) {
        console.error('Error fetching races:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserPosition = async () => {
      if (!user) return;
      try {
        const leaderboard = await getLeaderboard(0);
        setTotalParticipants(leaderboard.length);
        const entry = leaderboard.find(e => e.user_id === user.id);
        if (entry) {
          setUserPosition(entry.position);
          setUserPoints(entry.points);
        }
      } catch (error) {
        console.error('Error fetching user position:', error);
      }
    };

    fetchRaces();
    fetchUserPosition();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!nextSessionInfo?.sessionDate) return;

    const updateCountdown = () => {
      const now = new Date();
      const diff = nextSessionInfo.sessionDate - now;
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextSessionInfo]);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section met Checkered Flag Achtergrond */}
      <div className="relative overflow-hidden">
        {/* Checkered Flag Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/images/checkered-flag-blue.png')",
          }}
        />
        {/* Dark Overlay voor leesbaarheid */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a2332]/80 via-[#1a2332]/70 to-[#1a2332]/90" />

        <div className="relative max-w-7xl mx-auto px-4 py-16 text-center">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src="/images/logo.png" 
              alt="Formule 1 Pool" 
              className="h-48 md:h-64 w-auto mx-auto drop-shadow-2xl"
            />
          </div>

          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Voorspel de race uitslagen, kwalificaties en kampioenen.
            <br />
            Verzamel punten en win geweldige prijzen!
          </p>

          {!user && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  <Users className="mr-2" size={20} />
                  Registreren
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#FDB913] text-[#FDB913] hover:bg-[#FDB913] hover:text-[#1a2332] font-bold text-lg px-8 py-6 bg-transparent"
                >
                  Inloggen
                </Button>
              </Link>
            </div>
          )}

          {user && (
            <div className="flex flex-col items-center gap-4">
              <Link to="/voorspel">
                <Button
                  size="lg"
                  className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-bold text-lg px-8 py-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  <TrendingUp className="mr-2" size={20} />
                  Maak Je Voorspelling
                </Button>
              </Link>
              {userPosition && (
                <Link to="/stand" className="mt-2">
                  <div data-testid="user-position-badge" className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-[#FDB913]/30 rounded-full px-6 py-2 hover:bg-white/20 transition-colors cursor-pointer">
                    <Trophy size={18} className="text-[#FDB913]" />
                    <span className="text-gray-200 text-sm">
                      Jouw positie: <span className="text-[#FDB913] font-bold text-lg">#{userPosition}</span>
                      <span className="text-gray-400 mx-1">|</span>
                      <span className="text-white font-semibold">{userPoints}</span> <span className="text-gray-400">punten</span>
                      <span className="text-gray-400 mx-1">|</span>
                      <span className="text-gray-400">van {totalParticipants}</span>
                    </span>
                  </div>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Countdown Clock */}
        {nextSessionInfo && (
          <Card data-testid="countdown-clock" className="mb-12 border-2 border-[#FDB913] bg-gradient-to-br from-[#1a2332] to-[#2d3e50] text-white overflow-hidden">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-sm text-[#FDB913] font-semibold tracking-wider mb-1">VOLGENDE SESSIE</div>
                <div className="text-xl md:text-2xl font-bold mb-1">
                  {nextSessionInfo.race.name} — {nextSessionInfo.sessionLabel}
                </div>
                <div className="text-sm text-gray-300 mb-4">
                  {nextSessionInfo.sessionDate.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} om {nextSessionInfo.sessionDate.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                </div>

                {/* Countdown boxes */}
                <div className="flex justify-center gap-3 md:gap-4 mb-4">
                  {[
                    { value: countdown.days, label: 'DAGEN' },
                    { value: countdown.hours, label: 'UREN' },
                    { value: countdown.minutes, label: 'MIN' },
                    { value: countdown.seconds, label: 'SEC' },
                  ].map(({ value, label }) => (
                    <div key={label} className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-[60px] md:min-w-[72px]">
                      <div className="text-2xl md:text-3xl font-bold text-[#FDB913] tabular-nums">
                        {String(value).padStart(2, '0')}
                      </div>
                      <div className="text-[10px] md:text-xs text-gray-400 tracking-wider">{label}</div>
                    </div>
                  ))}
                </div>

                {/* Deadline status */}
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${
                  nextSessionInfo.deadlinePassed
                    ? 'bg-red-500/20 text-red-300'
                    : 'bg-green-500/20 text-green-300'
                }`}>
                  {nextSessionInfo.deadlinePassed ? (
                    <>
                      <AlertCircle size={14} />
                      <span>Deadline verstreken — voorspelling niet meer aan te passen</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={14} />
                      <span>Deadline nog niet verstreken — vul je voorspelling in!</span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="border-t-4 border-t-[#FDB913] hover:shadow-lg transition-shadow bg-card">
            <CardContent className="p-6 text-center">
              <div className="bg-[#FDB913] rounded-full p-4 inline-block mb-4">
                <TrendingUp size={32} className="text-[#1a2332]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-foreground">Voorspel Uitslagen</h3>
              <p className="text-muted-foreground text-sm">
                Voorspel race winnaars, top 5, kwalificatie resultaten en meer!
              </p>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-[#FDB913] hover:shadow-lg transition-shadow bg-card">
            <CardContent className="p-6 text-center">
              <div className="bg-[#FDB913] rounded-full p-4 inline-block mb-4">
                <Trophy size={32} className="text-[#1a2332]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-foreground">Verzamel Punten</h3>
              <p className="text-muted-foreground text-sm">
                Verdien punten voor correcte voorspellingen en klim in het klassement!
              </p>
            </CardContent>
          </Card>

          <Card className="border-t-4 border-t-[#FDB913] hover:shadow-lg transition-shadow bg-card">
            <CardContent className="p-6 text-center">
              <div className="bg-[#FDB913] rounded-full p-4 inline-block mb-4">
                <Award size={32} className="text-[#1a2332]" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-foreground">Win Prijzen</h3>
              <p className="text-muted-foreground text-sm">
                De beste voorspellers winnen geweldige prijzen/geldprijzen!
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Volgende Race */}
        {!loading && nextRace && (
          <Card 
            className="mb-12 border-2 border-[#FDB913] overflow-hidden relative"
            style={{
              backgroundImage: getRaceFlagUrl(nextRace) ? `url(${getRaceFlagUrl(nextRace)})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {/* Semi-transparent overlay for flag background */}
            <div className={`absolute inset-0 ${
              theme === 'dark'
                ? 'bg-gradient-to-r from-[#1a2332]/95 via-[#1a2332]/90 to-[#2d3e50]/85'
                : 'bg-gradient-to-r from-white/95 via-white/92 to-gray-50/90'
            }`} />
            
            <CardContent className="p-6 relative z-10">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  {/* Circuit Layout Icon */}
                  {getCircuitLayoutUrl(nextRace) ? (
                    <img 
                      src={getCircuitLayoutUrl(nextRace)} 
                      alt={nextRace.circuit_name || 'Circuit'}
                      className="w-24 h-16 object-contain"
                      style={{ 
                        // Local PNGs and uploads: Apply filter for both modes (gold in dark, dark blue in light)
                        // F1 media images: apply theme-appropriate filter
                        filter: (getCircuitLayoutUrl(nextRace).includes('/circuits/') || getCircuitLayoutUrl(nextRace).includes('/uploads/'))
                          ? (theme === 'dark' ? LOCAL_PNG_GOLD_FILTER : LOCAL_PNG_DARK_BLUE_FILTER)
                          : (theme === 'dark' ? FILTER_GOLD : FILTER_DARK_BLUE)
                      }}
                    />
                  ) : (
                    <span className="text-5xl">🏁</span>
                  )}
                  <div>
                    <h3 className="text-sm text-muted-foreground font-semibold">VOLGENDE RACE</h3>
                    <h2 className="text-2xl font-bold text-foreground">
                      {nextRace.name}
                    </h2>
                    <p className="text-muted-foreground">{nextRace.circuit_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-muted-foreground mb-1">Race start</div>
                  <div className="text-xl font-bold text-[#FDB913]">
                    {nextRace.date && nextRace.time
                      ? new Date(`${nextRace.date}T${nextRace.time}:00Z`).toLocaleDateString('nl-NL', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                        })
                      : 'TBD'
                    }
                  </div>
                  <div className="text-lg text-foreground">{formatLocalTime(nextRace.date, nextRace.time)}</div>
                </div>
                {user && (
                  <Link to="/voorspel">
                    <Button className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-bold">
                      <Target className="mr-2" size={16} />
                      Voorspel Nu
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard */}
        <div className="mb-12">
          <Leaderboard limit={10} />
          <div className="text-center mt-4">
            <Link to="/stand">
              <Button variant="outline" className="border-[#FDB913] text-[#FDB913] hover:bg-[#FDB913] hover:text-[#1a2332]">
                Bekijk Volledig Klassement
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
